import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CircularProgress, Divider, Stack, TextField, Typography } from "@mui/material";
import BackHeader from "../components/BackHeader";
import { Colors } from "../utils/colors";
import { API } from "../constant/apiConstant";

const currency = (value) => `₦${Number(value || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const panel = { bgcolor: "#092b49", color: "white", border: "1px solid rgba(255,255,255,.11)", borderRadius: 3, p: { xs: 2, sm: 3 }, minWidth: 0 };

export default function InfluencerDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError("");
      const query = new URLSearchParams();
      if (startDate) query.set("start_date", startDate);
      if (endDate) query.set("end_date", endDate);
      try {
        const response = await fetch(`${API.BASE_URL}/api/referrals/me?${query}`, { credentials: "include", signal: controller.signal });
        const result = await response.json();
        if (!response.ok || !result.status) throw new Error(result.message || "Unable to load your Influencer Dashboard.");
        setData(result);
      } catch (cause) { if (cause.name !== "AbortError") setError(cause.message); }
      finally { if (!controller.signal.aborted) setLoading(false); }
    };
    load();
    return () => controller.abort();
  }, [startDate, endDate]);

  const metrics = [
    ["Profiles referred", data?.summary?.profiles_referred || 0],
    ["Ticket purchases", data?.summary?.ticket_purchases_referred || 0],
    ["Tickets referred", data?.summary?.tickets_referred || 0],
    ["Referred ticket value", currency(data?.summary?.referred_ticket_value)],
    ["Commission earned", currency(data?.summary?.commission_earned)],
    ["Unpaid commission", currency(data?.summary?.available_commission)]
  ];
  const highest = Math.max(1, ...(data?.monthly || []).map((item) => Number(item.tickets || 0)));

  return <Box sx={{ minHeight: "100vh", bgcolor: Colors.secondary, color: "white", px: { xs: 2, sm: 3 }, py: 3 }}>
    <Box sx={{ maxWidth: 1000, mx: "auto", minWidth: 0 }}>
      <BackHeader title="Legacy influencer history" />
      <Typography sx={{ color: "#d5e3f1", mb: 3 }}>Historical agreements and earnings. Existing obligations are preserved pending an approved transition.</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <CircularProgress sx={{ color: Colors.accent, mb: 2 }} />}
      {data && <>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
          <TextField type="date" label="From" value={startDate} onChange={(event) => setStartDate(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: 1, bgcolor: "white", borderRadius: 1 }} />
          <TextField type="date" label="To" value={endDate} onChange={(event) => setEndDate(event.target.value)} InputLabelProps={{ shrink: true }} sx={{ flex: 1, bgcolor: "white", borderRadius: 1 }} />
          <Button onClick={() => { setStartDate(""); setEndDate(""); }} sx={{ color: Colors.accent }}>Clear dates</Button>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2,minmax(0,1fr))", md: "repeat(3,minmax(0,1fr))" }, gap: 2, mb: 3 }}>
          {metrics.map(([label, value]) => <Card key={label} sx={{ ...panel, p: { xs: 1.75, sm: 2.5 } }}>
            <Typography sx={{ color: "#b7ccdb", fontSize: 13 }}>{label}</Typography>
            <Typography sx={{ color: Colors.accent, fontWeight: 800, fontSize: { xs: "1.2rem", sm: "1.5rem" }, overflowWrap: "anywhere" }}>{value}</Typography>
          </Card>)}
        </Box>
        <Card sx={{ ...panel, mb: 3 }}>
          <Typography variant="h6" sx={{ color: Colors.accent, fontWeight: 800, mb: 2 }}>Ticket referral growth</Typography>
          {(data.monthly || []).length ? data.monthly.map((item) => <Box key={item.month} sx={{ mb: 1.5 }}>
            <Stack direction="row" justifyContent="space-between"><Typography>{item.month}</Typography><Typography sx={{ textAlign: "right" }}>{item.tickets} tickets / {currency(item.commission)} commission</Typography></Stack>
            <Box sx={{ height: 8, mt: 1, bgcolor: "#174665", borderRadius: 4 }}><Box sx={{ height: "100%", width: `${100 * Number(item.tickets || 0) / highest}%`, minWidth: item.tickets ? 8 : 0, bgcolor: Colors.primary, borderRadius: 4 }} /></Box>
          </Box>) : <Typography sx={{ color: "#b7ccdb" }}>Referral activity will appear here after your first successful referral.</Typography>}
        </Card>
        <Card sx={panel}>
          <Typography variant="h6" sx={{ color: Colors.accent, fontWeight: 800, mb: 2 }}>Recent referral activity</Typography>
          {(data.activity || []).length ? data.activity.map((item, index) => <Box key={`${item.created_at}-${index}`}>
            {index > 0 && <Divider sx={{ borderColor: "rgba(255,255,255,.1)", my: 1.5 }} />}
            <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
              <Box><Typography sx={{ fontWeight: 700 }}>{item.first_name || "Referred profile"}</Typography><Typography sx={{ color: "#b7ccdb", fontSize: 13 }}>{item.type === "PROFILE_CREATION" ? "Profile created" : `${item.ticket_quantity} tickets / ${currency(item.ticket_value)}`}</Typography></Box>
              <Box sx={{ textAlign: { sm: "right" } }}><Typography sx={{ color: Colors.primary, fontWeight: 800 }}>{currency(item.commission)}</Typography><Typography sx={{ color: "#b7ccdb", fontSize: 12 }}>{new Date(item.created_at).toLocaleDateString("en-NG")}</Typography></Box>
            </Stack>
          </Box>) : <Typography sx={{ color: "#b7ccdb" }}>No referrals in this period.</Typography>}
        </Card>
      </>}
    </Box>
  </Box>;
}
